import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Save, Loader2 } from "lucide-react";
import {
    adminApi,
    getAdminToken,
    clearAdminToken,
    API,
} from "../../lib/api";
import { toast } from "sonner";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

const SUBMISSION_STATUSES = [
    "received",
    "payment_verified",
    "under_review",
    "report_ready",
    "plan_delivered",
    "completed",
];

export default function SubmissionDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = getAdminToken();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("received");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [showScreenshot, setShowScreenshot] = useState(false);
    const [screenshotBlobUrl, setScreenshotBlobUrl] = useState("");
    const [loadingScreenshot, setLoadingScreenshot] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate("/admin");
            return;
        }
        adminApi(token)
            .get(`/admin/submissions/${id}`)
            .then((r) => {
                setData(r.data);
                setStatus(r.data.status || "received");
                setNotes(r.data.progress_notes || "");
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    clearAdminToken();
                    navigate("/admin");
                } else {
                    toast.error("Submission not found.");
                    navigate("/admin/dashboard");
                }
            })
            .finally(() => setLoading(false));
    }, [id, navigate, token]);

    const saveStatus = async () => {
        setSaving(true);
        try {
            const { data: updated } = await adminApi(token).patch(
                `/admin/submissions/${id}/status`,
                { status, notes },
            );
            setData(updated);
            toast.success("Progress updated.");
        } catch {
            toast.error("Update failed.");
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return (
            <div className="min-h-screen bg-[#F3EFE9] flex items-center justify-center">
                <div className="text-[#758684]">Loading…</div>
            </div>
        );
    if (!data) return null;

    const screenshotUrl = `${API}/admin/submissions/${id}/screenshot`;

    const toggleScreenshot = async () => {
        if (showScreenshot) {
            setShowScreenshot(false);
            return;
        }
        if (!screenshotBlobUrl) {
            setLoadingScreenshot(true);
            try {
                const res = await fetch(screenshotUrl, {
                    headers: { "X-Admin-Password": token },
                });
                if (!res.ok) throw new Error("fetch failed");
                const blob = await res.blob();
                setScreenshotBlobUrl(URL.createObjectURL(blob));
            } catch {
                toast.error("Could not load screenshot.");
                setLoadingScreenshot(false);
                return;
            } finally {
                setLoadingScreenshot(false);
            }
        }
        setShowScreenshot(true);
    };

    const downloadScreenshot = async () => {
        try {
            const res = await fetch(screenshotUrl, {
                headers: { "X-Admin-Password": token },
            });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = data?.screenshot_filename || "screenshot";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed.");
        }
    };

    return (
        <div className="min-h-screen bg-[#F3EFE9]">
            <div className="bg-white border-b border-[#E6DFD3]">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <Link
                        to="/admin/dashboard"
                        className="inline-flex items-center gap-2 text-sm text-[#4A5A58] hover:text-[#1A5C53]"
                    >
                        <ArrowLeft size={16} /> Back to dashboard
                    </Link>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#758684]">
                        {data.package} submission
                    </span>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-6">
                <div className="bg-white border border-[#E6DFD3] rounded-2xl p-8 md:p-10">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="font-serif text-3xl text-[#0D1B1A]">
                                {data.name}
                            </h1>
                            <p className="text-sm text-[#4A5A58] mt-1">
                                {data.email} · {data.phone}
                            </p>
                            <p className="text-xs text-[#758684] mt-2 font-mono break-all">
                                ID: {data.id}
                            </p>
                        </div>
                        <div className="text-right">
                            <Badge className="bg-[#1A5C53]/10 text-[#1A5C53] border-[#1A5C53]/20">
                                {data.status.replace(/_/g, " ")}
                            </Badge>
                            <div className="text-xs text-[#758684] mt-2">
                                {new Date(data.created_at).toLocaleString(
                                    "en-IN",
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress management */}
                <div className="bg-white border border-[#E6DFD3] rounded-2xl p-8">
                    <h2 className="font-serif text-2xl text-[#0D1B1A]">
                        Client Progress
                    </h2>
                    <div className="mt-5 grid md:grid-cols-3 gap-5">
                        <div>
                            <Label>Status</Label>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger
                                    data-testid="detail-status-select"
                                    className="mt-1.5 bg-white border-[#E6DFD3]"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SUBMISSION_STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s.replace(/_/g, " ")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2">
                            <Label>Note to client (shown on Track page)</Label>
                            <Textarea
                                data-testid="detail-notes-input"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                                placeholder="e.g., We've verified your payment and started reviewing..."
                            />
                        </div>
                    </div>
                    <button
                        onClick={saveStatus}
                        disabled={saving}
                        data-testid="detail-save-btn"
                        className="mt-5 inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] rounded-md px-5 py-2.5 font-medium disabled:opacity-60"
                    >
                        {saving ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Save size={16} />
                        )}
                        Save progress
                    </button>
                </div>

                {/* Payment screenshot */}
                <div className="bg-white border border-[#E6DFD3] rounded-2xl p-8">
                    <div className="flex items-center justify-between">
                        <h2 className="font-serif text-2xl text-[#0D1B1A] flex items-center gap-2">
                            <ImageIcon size={18} /> Payment Screenshot
                        </h2>
                        {data.screenshot_filename && (
                            <button
                                onClick={toggleScreenshot}
                                disabled={loadingScreenshot}
                                data-testid="toggle-screenshot"
                                className="text-sm text-[#1A5C53] hover:underline disabled:opacity-60"
                            >
                                {loadingScreenshot
                                    ? "Loading…"
                                    : showScreenshot
                                      ? "Hide"
                                      : "View"}
                            </button>
                        )}
                    </div>
                    {!data.screenshot_filename ? (
                        <p className="mt-4 text-sm text-[#758684]">
                            No screenshot on file.
                        </p>
                    ) : (
                        showScreenshot &&
                        screenshotBlobUrl && (
                            <div className="mt-5">
                                <img
                                    src={screenshotBlobUrl}
                                    alt="Payment screenshot"
                                    className="max-w-md rounded-xl border border-[#E6DFD3]"
                                />
                                <button
                                    onClick={downloadScreenshot}
                                    className="mt-3 inline-block text-sm text-[#1A5C53] underline"
                                >
                                    Download file
                                </button>
                            </div>
                        )
                    )}
                </div>

                {/* Answers */}
                <div className="bg-white border border-[#E6DFD3] rounded-2xl p-8">
                    <h2 className="font-serif text-2xl text-[#0D1B1A] mb-6">
                        Answers
                    </h2>
                    <dl className="space-y-5">
                        {Object.entries(data.answers || {}).map(([k, v]) => (
                            <div
                                key={k}
                                className="grid md:grid-cols-3 gap-4 pb-5 border-b border-[#E6DFD3]/60 last:border-0"
                            >
                                <dt className="text-xs uppercase tracking-[0.15em] text-[#758684]">
                                    {k.replace(/_/g, " ")}
                                </dt>
                                <dd className="md:col-span-2 text-[#0D1B1A] whitespace-pre-wrap">
                                    {String(v) || "—"}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
}
